/**
 * Emails promised when a Sponsor's subscription ends.
 * Advance notice, the end notice, the day-53 reminder, and the deletion confirmation.
 * Browser-safe. CommonJS only. No object spread.
 */

const { PACARION_POSTAL_ADDRESS } = require('./sponsorlessBrand');
const { formatEmailDate } = require('../utils/dates');
const { billingNoticeRecipient } = require('./billingNoticeGate');

const WINDOW_DAYS = 60;
const DAY53_MIN = 5;
const DAY53_MAX = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
const ENDED_STATUSES = {
  canceled: true,
  cancelled: true,
  unpaid: true,
  incomplete_expired: true,
};
const KEPT_PLAN = { active: true, trialing: true };

function atMs(now) {
  if (now instanceof Date) return now.getTime();
  return new Date(now || Date.now()).getTime();
}

function addDays(iso, days) {
  const start = new Date(iso).getTime();
  if (Number.isNaN(start)) return '';
  return new Date(start + (days * DAY_MS)).toISOString();
}

function daysUntil(iso, now) {
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  return Math.floor((end - atMs(now)) / DAY_MS);
}

function formatLandingDate(iso) {
  return formatEmailDate(iso);
}

function landingRecipient(email, env) {
  return billingNoticeRecipient(email, env);
}

function sponsorEnded(row, now) {
  const status = String((row && row.status) || '').toLowerCase();
  if (ENDED_STATUSES[status]) return true;
  if (row && row.cancelAtPeriodEnd && row.periodEnd) {
    const days = daysUntil(row.periodEnd, now);
    if (days !== null && days < 0) return true;
  }
  return false;
}

function windowEndFor(row) {
  if (!row || !row.periodEnd) return '';
  return addDays(row.periodEnd, WINDOW_DAYS);
}

function planKept(row) {
  const status = String((row && row.planStatus) || '').toLowerCase();
  return KEPT_PLAN[status] === true;
}

function addressLine() {
  return `Pacarion LLC, ${PACARION_POSTAL_ADDRESS}. support@pacarion.com.`;
}

function sponsorLandingCopy(kind, fields) {
  const sponsor = String((fields && fields.sponsorName) || 'Your sponsor').trim() || 'Your sponsor';
  const first = String((fields && fields.firstName) || '').trim();
  const hello = first ? `Hi ${first},` : 'Hi,';
  const endDate = formatLandingDate(fields && fields.periodEnd);
  const windowEnd = formatLandingDate(fields && fields.windowEnd);
  const price = '$4.99/month or $49/year';
  const exportLine = 'More → Download my data';
  if (kind === 'advance') {
    return {
      subject: `${sponsor}'s Pacarion access to your home records ends on ${endDate}`,
      paragraphs: [
        `${hello} ${sponsor} has scheduled their Pacarion subscription to end on ${endDate}. After that date, ${sponsor} will no longer have access to your home records. Your records are yours, and nothing is deleted on that date. From ${endDate} you'll have 60 days (until ${windowEnd}) to choose what's next: (a) accept an invitation from another sponsor and transfer your home record, or (b) keep your account by buying the Homeowner plan on app.pacarion.com (${price}, renewing automatically at the same price until you cancel; full terms are shown before you subscribe). If you buy it, full access comes back right away. You can export your home records, photos, and documents anytime: ${exportLine}.`,
        addressLine(),
      ],
    };
  }
  if (kind === 'end') {
    const kept = fields && fields.kept;
    const choice = kept
      ? 'You already have a Homeowner plan, so your account stays.'
      : `To keep using Pacarion, you can (a) accept an invitation from another sponsor and transfer your home record, or (b) keep your account by buying the Homeowner plan on app.pacarion.com (${price}, renewing automatically at the same price until you cancel). If you buy it, full access comes back right away. If you do neither by ${windowEnd}, your account and data will be deleted. We'll remind you 7 days before.`;
    return {
      subject: "Your Pacarion home records: 60 days to choose what's next",
      paragraphs: [
        `${hello} ${sponsor}'s Pacarion subscription ended on ${endDate}, and ${sponsor} no longer has access to your home records. Your records are yours. Until ${windowEnd} (60 days) you have read-only access and can export your home records, photos, and documents anytime: ${exportLine}. ${choice}`,
        addressLine(),
      ],
    };
  }
  if (kind === 'day53') {
    const days = Number(fields && fields.daysLeft) || DAY53_MAX;
    return {
      subject: `Reminder: your Pacarion home records will be deleted on ${windowEnd}`,
      paragraphs: [
        `${hello} in ${days} days, on ${windowEnd}, your Pacarion account and home records will be deleted. To keep them, export now (${exportLine}), accept an invitation from a participating sponsor, or buy the Homeowner plan on app.pacarion.com (${price}; the terms are shown before you subscribe). If you do nothing, your records will be permanently deleted.`,
        addressLine(),
      ],
    };
  }
  return {
    subject: 'Your Pacarion account has been deleted',
    paragraphs: [
      'Your Pacarion account and home records have been deleted as scheduled. Questions: support@pacarion.com.',
      addressLine(),
    ],
  };
}

function landingNoticeKey(kind, clientId, stamp) {
  return `homeowner_sponsor_${kind}:${clientId}:${stamp || 'open'}`;
}

function sponsorLandingNotices(row, now) {
  const when = now instanceof Date ? now : new Date(now || Date.now());
  if (!row || !row.clientId || !row.periodEnd) return [];
  const windowEnd = windowEndFor(row);
  const kept = planKept(row);
  const ended = sponsorEnded(row, when);
  const daysLeft = daysUntil(windowEnd, when);
  const notices = [];
  if (!ended && row.cancelAtPeriodEnd) {
    const untilEnd = daysUntil(row.periodEnd, when);
    if (untilEnd !== null && untilEnd >= 0) {
      notices.push({
        kind: 'advance',
        noticeKey: landingNoticeKey('advance', row.clientId, row.periodEnd),
        copy: sponsorLandingCopy('advance', {
          sponsorName: row.sponsorName,
          firstName: row.firstName,
          periodEnd: row.periodEnd,
          windowEnd,
        }),
      });
    }
  }
  if (ended && daysLeft !== null && daysLeft > 0) {
    notices.push({
      kind: 'end',
      noticeKey: landingNoticeKey('end', row.clientId, row.periodEnd),
      copy: sponsorLandingCopy('end', {
        sponsorName: row.sponsorName,
        firstName: row.firstName,
        periodEnd: row.periodEnd,
        windowEnd,
        kept,
      }),
    });
  }
  if (ended && !kept && daysLeft !== null && daysLeft >= DAY53_MIN && daysLeft <= DAY53_MAX) {
    notices.push({
      kind: 'day53',
      noticeKey: landingNoticeKey('day53', row.clientId, windowEnd),
      copy: sponsorLandingCopy('day53', {
        firstName: row.firstName,
        windowEnd,
        daysLeft,
      }),
    });
  }
  if (ended && !kept && daysLeft !== null && daysLeft <= 0) {
    notices.push({
      kind: 'deleted',
      noticeKey: landingNoticeKey('deleted', row.clientId, windowEnd),
      copy: sponsorLandingCopy('deleted', {}),
      deleteAccount: true,
    });
  }
  return notices;
}

module.exports = {
  WINDOW_DAYS,
  sponsorLandingCopy,
  sponsorLandingNotices,
  landingNoticeKey,
  landingRecipient,
  windowEndFor,
  formatLandingDate,
};
